import { useState } from "react";
import { Link } from "react-router-dom";
import { useListMyCryptoPayoutsQuery } from "../../../features/cryptoPayouts/cryptoPayoutsSlice";
import {
  STATUS_LABEL,
  STATUS_CLASS,
  assetUnit,
  fmtNaira,
} from "../../../features/cryptoPayouts/status";

interface Props {
  title?: string;
  limit?: number;
}

// Reusable "Crypto Payouts" history table — RTK-sourced, embedded in the
// withdrawal history and transaction history views. Each row links to the
// dedicated payout detail page.
const PER_PAGE = 6;

export const CryptoPayoutsTablePanel = ({ title = "Crypto Payouts", limit }: Props) => {
  const { data, isLoading, isFetching } = useListMyCryptoPayoutsQuery();
  const [page, setPage] = useState(0);
  const all = data ?? [];
  // explicit `limit` = embedded preview (no pager); otherwise paginate 6/page
  const rows = limit ? all.slice(0, limit) : all.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
  const pageCount = Math.max(1, Math.ceil(all.length / PER_PAGE));

  return (
    <div className="bg-black flex flex-col gap-4 px-4 lg:px-10 py-10">
      <h3 className="text-primary text-xl font-medium">{title}</h3>

      <div className="overflow-x-auto relative">
        {isLoading || isFetching ? (
          <div className="w-full text-white/40 my-16 text-center">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="w-full text-white/40 my-16 text-center">
            No crypto payouts found
          </div>
        ) : (
          <table className="w-full text-sm text-left text-gray-400">
            <thead className="text-xs uppercase bg-gray-700 text-gray-300">
              <tr>
                <th className="py-3 px-6">Reference</th>
                <th className="py-3 px-6">Asset</th>
                <th className="py-3 px-6">Destination</th>
                <th className="py-3 px-6">Amount (₦)</th>
                <th className="py-3 px-6">You receive</th>
                <th className="py-3 px-6">Status</th>
                <th className="py-3 px-6">Date</th>
                <th className="py-3 px-6"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p._id} className="border-b bg-transparent border-gray-700">
                  <td className="py-4 px-6 font-medium text-white whitespace-nowrap">
                    {p.payoutReference}
                  </td>
                  <td className="py-4 px-6">{assetUnit(p.asset)}</td>
                  <td className="py-4 px-6 max-w-[180px] truncate" title={p.destinationAddress}>
                    {p.destinationAddress}
                  </td>
                  <td className="py-4 px-6">{fmtNaira(p.nairaAmount)}</td>
                  <td className="py-4 px-6 whitespace-nowrap">
                    {p.cryptoUnits} {assetUnit(p.asset)}
                  </td>
                  <td className="py-4 px-6">
                    <span className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${STATUS_CLASS[p.status]}`}>
                      {STATUS_LABEL[p.status]}
                    </span>
                  </td>
                  <td className="py-4 px-6 whitespace-nowrap">
                    {new Date(p.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-4 px-6">
                    <Link to={`/app/crypto-payout/${p._id}`}>
                      <button className="text-blue-500 text-xs">Details</button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!limit && all.length > PER_PAGE && (
        <div className="flex items-center justify-between text-sm">
          <button
            className="px-3 py-1.5 rounded bg-white/5 text-white/70 disabled:opacity-30 hover:bg-white/10 transition-colors"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            ← Prev
          </button>
          <span className="text-white/40 text-xs">
            Page {page + 1} of {pageCount} · {all.length} payouts
          </span>
          <button
            className="px-3 py-1.5 rounded bg-white/5 text-white/70 disabled:opacity-30 hover:bg-white/10 transition-colors"
            disabled={page >= pageCount - 1}
            onClick={() => setPage(page + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};
